import { withNamespaces } from "react-i18next";
import { Badge, Card, CardBody, CardText, CardTitle, UncontrolledTooltip } from "reactstrap";
import {
    RiMapPinLine,
    RiPlantLine,
    RiErrorWarningLine,
    RiCheckLine,
    RiShapeLine,
    RiArrowDownLine,
    RiInformationLine
} from "react-icons/ri";

const RiskAssessmentFlow = ({ t, deforestationCheckNotNeeded }) => {
    return (
        <Card className="h-100">
            <CardBody>
                <CardTitle tag="h5" className="mb-3">
                    <span className="me-1">
                        {t('Risk Assessment Graph')}
                    </span>

                    <i
                        className="ri-information-line"
                        style={{
                            cursor: 'pointer'
                        }}
                        id="risk-assessment-flow-help-text"
                    />

                    <UncontrolledTooltip
                        target="risk-assessment-flow-help-text"
                    >
                        {deforestationCheckNotNeeded ? (
                            t('This parcel has a consistent low risk status. Only the first two steps of the GeoCledian process were needed, and no further deforestation risk assessment was required. Check icons indicate completed steps, and the final step summarizes the confirmed risk status.')
                        ) : (
                            <>
                                {t('Shows the four-step GeoCledian process for the selected parcel.')}
                                <br />
                                {t('Check icons indicate completed steps, and the flow highlights where deforestation risk is detected.')}
                            </>
                        )}
                    </UncontrolledTooltip>
                </CardTitle>

                <div className="position-relative">
                    <Card 
                        className="shadow-none card border-left mb-3 position-relative"
                        style={{
                            border: '1px solid #dcdcdc',
                            borderLeft: '4px solid #209a1e'
                        }}
                    >
                        <div className="d-flex justify-content-center align-items-center">
                            <div style={{
                                width: '40px'
                            }}
                                className="d-flex justify-content-center align-items-center m-3"
                            >
                                <RiMapPinLine size="30" />
                            </div>

                            <CardBody className="p-3 d-flex align-items-center justify-content-between">
                                <div>
                                    <CardTitle tag="h5">
                                        <RiCheckLine size={24} className="text-success" />
                                        {t('Obtain Geolocation')}
                                    </CardTitle>
                                    <CardText>
                                        {t('Supporting collection by requesting data from your supplier')}
                                    </CardText>
                                </div>
                            </CardBody>
                        </div>

                        <RiArrowDownLine
                            size={30}
                            style={{
                                position: 'absolute',
                                right: '72px',
                                zIndex: '10',
                                top: '78px'
                            }}
                            color="#74788d"
                        />
                    </Card>

                    <Card 
                        className="shadow-none card border-left mb-3 position-relative"
                        style={{
                            border: '1px solid #dcdcdc',
                            borderLeft: '4px solid #209a1e',
                            marginLeft: '20px'
                        }}
                    >
                        <div className="d-flex justify-content-center align-items-center">
                            <div style={{
                                width: '40px'
                            }}
                                className="d-flex justify-content-center align-items-center m-3"
                            >
                                <RiShapeLine size="30" />
                            </div>

                            <CardBody className="p-3 d-flex align-items-center justify-content-between">
                                <div>
                                    <CardTitle tag="h5">
                                        <RiCheckLine size={24} className="text-success" />
                                        {t('Geometry & Batch Validation')}
                                    </CardTitle>
                                    <CardText>
                                        {t('Supplier provided geolocation technical & plausibility check')}
                                    </CardText>
                                </div>

                                <Badge className="bg-secondary">
                                    {t('Geolocation Compliance Done')}
                                </Badge>
                            </CardBody>
                        </div>

                         <RiArrowDownLine
                            size={30}
                            style={{
                                position: 'absolute',
                                right: '72px',
                                zIndex: '10',
                                top: '78px'
                            }}
                            color="#74788d"
                        />
                    </Card>

                    {deforestationCheckNotNeeded ? (
                        <>
                            <Card
                                className="shadow-none card border-left mb-0 bg-soft-success"
                                style={{
                                    border: '1px solid #dcdcdc',
                                    borderLeft: '4px solid #209a1e',
                                    marginLeft: '60px'
                                }}
                            >
                                <div className="d-flex justify-content-center align-items-center">
                                    <div style={{
                                        width: '40px'
                                    }}
                                        className="d-flex justify-content-center align-items-center m-3"
                                    >
                                        <RiInformationLine size="30" />
                                    </div>

                                    <CardBody className="p-3 d-flex align-items-center justify-content-between">
                                        <div>
                                            <CardTitle tag="h5">
                                                <RiCheckLine size={24} className="text-success" />
                                                {t('Parcel risk status confirmed')}
                                            </CardTitle>
                                            <CardText>
                                                {t('No further assessment needed')}
                                            </CardText>
                                        </div>
                                    </CardBody>
                                </div>
                            </Card>
                        </>
                    ) : (
                        <>
                            <Card
                                className="shadow-none card border-left mb-3 position-relative"
                                style={{
                                    border: '1px solid #dcdcdc',
                                    borderLeft: '4px solid #209a1e',
                                    marginLeft: '40px'
                                }}
                            >
                                <div className="d-flex justify-content-center align-items-center">
                                    <div style={{
                                        width: '40px'
                                    }}
                                        className="d-flex justify-content-center align-items-center m-3"
                                    >
                                        <RiPlantLine size="30" />
                                    </div>

                                    <CardBody className="p-3 d-flex align-items-center justify-content-between">
                                        <div>
                                            <CardTitle tag="h5">
                                                <RiCheckLine size={24} className="text-success" />
                                                {t('Regional Deforestation Risk Assessment')}
                                            </CardTitle>
                                            <CardText>
                                                {t('Determine general deforestation risk (Article 10 & 13.2)')}
                                            </CardText>
                                        </div>

                                        <Badge className="bg-secondary">
                                            {t('Risk Assessment Done')}
                                        </Badge>
                                    </CardBody>
                                </div>

                                <RiArrowDownLine
                                    size={30}
                                    style={{
                                        position: 'absolute',
                                        right: '72px',
                                        zIndex: '10',
                                        top: '78px'
                                    }}
                                    color="#74788d"
                                />
                            </Card>

                            <Card
                                className="shadow-none card border-left mb-0"
                                style={{
                                    border: '1px solid #dcdcdc',
                                    borderLeft: '4px solid rgb(255, 61, 96)',
                                    marginLeft: '60px'
                                }}
                            >
                                <div className="d-flex justify-content-center align-items-center">
                                    <div style={{
                                        width: '40px'
                                    }}
                                        className="d-flex justify-content-center align-items-center m-3"
                                    >
                                        <RiErrorWarningLine size="30" />
                                    </div>

                                    <CardBody className="p-3 d-flex align-items-center justify-content-between">
                                        <div>
                                            <CardTitle tag="h5">
                                                <RiCheckLine size={24} className="text-success" />
                                                {t('Geolocation Deforestation Risk Assessment')}
                                            </CardTitle>
                                            <CardText>
                                                {t('Deforestation free test protocol (Art.3a)')}
                                            </CardText>
                                        </div>

                                        <Badge className="bg-danger">
                                            {t('High Deforestation Risk')}
                                        </Badge>
                                    </CardBody>
                                </div>
                            </Card>
                        </>
                    )}
                </div>
            </CardBody>
        </Card>
    )
}

export default withNamespaces()(
    RiskAssessmentFlow
);